// Library methods
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTheme, Theme } from "@mui/material/styles";
import { PlusIcon } from "src/assets/icons";
import { ACCENT_TEAL } from "../ChoiceCard/ChoiceCard";

// Styles
import {
  ModalCustomConfirmTeal,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

const getSectionBackground = (theme: Theme) =>
  theme.palette.mode === "dark" ? "#00000090" : "rgba(255, 255, 255, 0.55)";

type HubSectionType = {
  id: string;
  icon: ReactNode;
  title: string;
  description: string;
  addLabel: string;
  canAdd: boolean;
  onAdd: () => void;
  isLoading: boolean;
  isEmpty: boolean;
  emptyText: string;
  children: ReactNode;
};

const HubSection = ({
  id,
  icon,
  title,
  description,
  addLabel,
  canAdd,
  onAdd,
  isLoading,
  isEmpty,
  emptyText,
  children,
}: HubSectionType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface, mutedText } = getSurfaceTokens(theme);

  return (
    <Box
      component="section"
      aria-labelledby={`${id}-title`}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        padding: "16px",
        borderRadius: "8px",
        backgroundColor: getSectionBackground(theme),
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap="12px"
        flexWrap="wrap"
      >
        <Box minWidth={0} flex="1 1 320px">
          <Box display="flex" alignItems="center" gap="8px" color={ACCENT_TEAL}>
            {icon}
            <Typography
              component="h2"
              id={`${id}-title`}
              fontSize="16px"
              fontWeight={600}
              color={ACCENT_TEAL}
            >
              {title}
            </Typography>
          </Box>
          <Typography
            fontSize="13px"
            color={mutedText}
            mt="4px"
            maxWidth="720px"
            lineHeight={1.45}
          >
            {description}
          </Typography>
        </Box>
        {canAdd && (
          <Button
            startIcon={<PlusIcon />}
            sx={ModalCustomConfirmTeal}
            disabled={isLoading}
            onClick={onAdd}
          >
            {addLabel}
          </Button>
        )}
      </Box>

      {isLoading && (
        <Typography fontSize="14px" color={onSurface}>
          {t("Loading...")}
        </Typography>
      )}
      {!isLoading && isEmpty && (
        <Typography fontSize="14px" color={onSurface}>
          {emptyText}
        </Typography>
      )}
      {!isLoading && !isEmpty && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fill, minmax(min(100%, 320px), 1fr))",
            gap: "10px",
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
};

export default HubSection;
